/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { setBasicDialogOpenFor, setConfirmDialog, setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useAddEditEmailGroup } from "./hooks";
import { useStoreDispatch } from "../../../../store/hooks";
import { ISnmpGroup } from "../../../../store/api/admin/types";
import { Trash2 } from "lucide-react";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../../globals/constants";
import { sanitizeHtmlInput } from "../../../../utils/helpers";

interface IAddEditAlarmForwardProps {
  selectedSnmpGroup?: ISnmpGroup;
  modelOpenMode?: string;
}

const AddEditEmailGroup: React.FC<IAddEditAlarmForwardProps> = ({ modelOpenMode = "Add", selectedSnmpGroup }) => {
  const dispatch = useStoreDispatch();
  const { formData, setFormData, errMsg, setErrMsg, submitHandler, isCreateEmailGroup, onBlurHandler, handleDeleteEmailGroup } = useAddEditEmailGroup();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const getValue = (val: string) => {
    return val?.includes(",");
  };

  const handleChange = (event: any) => {
    event.preventDefault();
    const { name, value, checked } = event.target;
    if (value === "on" || value === "off") {
      setFormData({ ...formData, [name]: checked });
    } else {
      if (name === "email") {
        let newValue: any;
        if (getValue(value)) {
          newValue = value?.split(",");
          setFormData({ ...formData, [name]: newValue });
          setErrMsg({ ...errMsg, [name]: "" });
        }
      }
      setFormData({ ...formData, [name]: value });
      setErrMsg({ ...errMsg, [name]: "" });
      dispatch(setErrorMsg({ ...errMsg, [name]: "" }));
    }
  };

  const handleClose = () => {
    dispatch(setErrorMsg({}));
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
    dispatch(setConfirmDialog("SHEET_EMAIL_DELETE"));
  };

  const handleConfirmDelete = async (actionName: string) => {
    setShowDeleteConfirm(false);
    if (!actionName) return;
    if (actionName.match("delete") && formData?.parentKey) {
      await handleDeleteEmailGroup(sanitizeHtmlInput(formData.parentKey), actionName);
      handleClose();
    }
  };

  const footer = (
    <div className="flex items-center justify-between w-full">
      {!isCreateEmailGroup ? (
        <Button variant="outline" className="!text-destructive hover:!text-destructive !border-destructive" onClick={handleDelete}>
          <Trash2 className="h-4 w-4" fill="currentColor" />
          Delete Group
        </Button>
      ) : <div />}
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={submitHandler}>
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {showDeleteConfirm && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleConfirmDelete}>
          Are you sure you want to delete <strong>{sanitizeHtmlInput(formData?.parentKey) || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <Sheet open={true} onClose={handleClose} title={`${modelOpenMode} Emails Group`} footer={footer} width="520px">
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Name */}
          <div className="space-y-4">
            <Label htmlFor="email-group-name">
              Name <span style={{ color: "var(--destructive)" }}>*</span>
            </Label>
            <Input
              id="email-group-name"
              name="parentKey"
              disabled={!isCreateEmailGroup}
              value={formData?.parentKey}
              onChange={handleChange}
              onBlur={onBlurHandler}
              maxLength={100}
              placeholder="Enter name"
              tooltipText="Maximum length: 100 characters"
              autoFocus
            />
            {errMsg?.parentKey && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.parentKey}</p>}
          </div>

          {/* Email Addresses */}
          <div className="space-y-4">
            <Label htmlFor="email-group-email">
              Email Addresses (separated by commas) <span style={{ color: "var(--destructive)" }}>*</span>
            </Label>
            <div>
              <p className="text-xs text-muted-foreground">Enter one address per line, or separate with commas or semicolons.</p>
              <textarea
                id="email-group-email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                onBlur={onBlurHandler}
                maxLength={150}
                placeholder="Email Addresses (separated by commas)"
                rows={4}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              />
            </div>
            {errMsg?.email && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.email}</p>}
          </div>
        </div>
      </form>
    </Sheet>
    </>
  );
};

export default AddEditEmailGroup;
